import { addDays, isAfter, isBefore, isSameDay, isValid, parse, startOfDay, startOfMonth, subYears } from 'date-fns';
import { __, allPass, always, gt, ifElse, length as ramdaLength, lte } from 'ramda';

import createValidationFn from '@gef-ui/form-core/validations/createValidationFn';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { dayMonthsAcceptedFormats } from '../constants';

export const isLength = createValidationFn(
	({
		length = null,
		maxLength = null,
		minLength = null,
		lengthMessageKey = 'validation:length',
		maxLengthMessageKey = 'validation:maxLength',
		minLengthMessageKey = 'validation:minLength',
	}) =>
		({ fieldValue }) => {
			if (length && ramdaLength(fieldValue) !== length) {
				return lengthMessageKey;
			} else if (maxLength && ramdaLength(fieldValue) > maxLength) {
				return maxLengthMessageKey;
			} else if (minLength && ramdaLength(fieldValue) < minLength) {
				return minLengthMessageKey;
			}
			return null;
		},
);

export const isDateWithOrder = createValidationFn(
	({ fromFieldName, toFieldName, messageKey = 'validation:datesWrongOrder', type = 'fullDate' }) =>
		({ formData }) => {
			const generateDateObjFromDateLikeField = (fieldValue) => {
				// valid fieldValue length is either 4 or 5
				if (fieldValue.length === 5) {
					return fieldValue.split(/[.\/]/);
				} else {
					return fieldValue.match(/(..)/g);
				}
			};

			if (formData[fromFieldName] && formData[toFieldName]) {
				let from;
				let to;
				switch (type) {
					case 'fullDate':
						from = new Date(formData[fromFieldName]);
						to = new Date(formData[toFieldName]);
						break;
					case 'year':
						from = parse(formData[fromFieldName], 'yyyy', new Date());
						to = parse(formData[toFieldName], 'yyyy', new Date());
					case 'dateLikeString':
						const isFromValid = new RegExp(dayMonthsAcceptedFormats).test(formData[fromFieldName]);
						const isToValid = new RegExp(dayMonthsAcceptedFormats).test(formData[toFieldName]);
						if (isFromValid && isToValid) {
							const [fromDd, fromMm] = generateDateObjFromDateLikeField(formData[fromFieldName]);
							const [toDd, toMm] = generateDateObjFromDateLikeField(formData[toFieldName]);

							// year is random here cause its not important, but its required in Date constructor
							from = new Date(2000, Number(fromMm) - 1, fromDd);
							to = new Date(2000, Number(toMm) - 1, toDd);
						} else {
							return null;
						}
				}
				if (isAfter(from, to)) {
					return messageKey;
				}
			} else {
				return null;
			}
		},
);

export const isDateLessThanYearInPastExactlyFromChosenDayOrNotInFuture = createValidationFn(
	({ messageKey = 'validation:dateMoreThanYearInPastOrInFuture' }) =>
		({ fieldValue }) => {
			if (!fieldValue) {
				return null;
			}

			const fieldDate = new Date(fieldValue);
			const dateToCompareSameOrAfter = startOfDay(subYears(new Date(), 1));
			const today = new Date();

			const dateIsValid =
				(isSameDay(fieldDate, dateToCompareSameOrAfter) || isAfter(fieldDate, dateToCompareSameOrAfter)) &&
				(isSameDay(fieldDate, today) || isBefore(fieldDate, today));

			if (!dateIsValid) {
				return messageKey;
			} else {
				return null;
			}
		},
);

export const isDateLessThanYearInPastConsideringWholeStartingMonthOrNotInFuture = createValidationFn(
	({ messageKey = 'validation:dateMoreThanYearInPastConsideringWholeStartingMonthOrInFuture' }) =>
		({ fieldValue }) => {
			if (!fieldValue) {
				return null;
			}

			const fieldDate = new Date(fieldValue);
			const dateToCompareSameOrAfter = startOfMonth(subYears(new Date(), 1));
			const today = new Date();

			const dateIsValid =
				(isSameDay(fieldDate, dateToCompareSameOrAfter) || isAfter(fieldDate, dateToCompareSameOrAfter)) &&
				(isSameDay(fieldDate, today) || isBefore(fieldDate, today));

			if (!dateIsValid) {
				return messageKey;
			} else {
				return null;
			}
		},
);

export const isDateLessThanXYearsInPastExactlyFromChosenDay = createValidationFn(
	({ yearsToSubtract }) =>
		({ fieldValue }) => {
			if (!fieldValue) {
				return null;
			}

			const fieldDate = new Date(fieldValue);
			const dateToCompare = startOfDay(subYears(new Date(), yearsToSubtract));
			const dateIsValid = isSameDay(fieldDate, dateToCompare) || isAfter(fieldDate, dateToCompare);

			const messageKey = getLabelMessage('validation:dateMoreThanXYearsInPast', { count: yearsToSubtract });

			if (!dateIsValid) {
				return messageKey;
			} else {
				return null;
			}
		},
);

export const isDateInFuture = createValidationFn(() => ({ fieldValue }) => {
	if (!fieldValue) {
		return null;
	}

	const tommorow = startOfDay(addDays(new Date(), 1));
	const dateIsValid = isAfter(new Date(fieldValue), tommorow) || isSameDay(new Date(fieldValue), tommorow);

	const messageKey = getLabelMessage('validation:dateMustBeInFuture');

	if (!dateIsValid) {
		return messageKey;
	} else {
		return null;
	}
});

export const isDateNotInFuture = createValidationFn(() => ({ fieldValue }) => {
	if (!fieldValue) {
		return null;
	}

	const today = startOfDay(new Date());
	const dateIsValid = isBefore(new Date(fieldValue), today) || isSameDay(new Date(fieldValue), today);

	const messageKey = getLabelMessage('validation:dateInFutureBasic');

	if (!dateIsValid) {
		return messageKey;
	} else {
		return null;
	}
});

export const isFullySpecifiedDate = createValidationFn(() => ({ fieldValue }) => {
	if (!fieldValue) {
		return null;
	}

	const dateIsValid = isValid(fieldValue);

	const messageKey = getLabelMessage('validation:dateIsNotComplete');

	if (!dateIsValid) {
		return messageKey;
	} else {
		return null;
	}
});

export const isDateAfterSpecifiedDateField = createValidationFn(
	({ field, fieldLocalization, messageKey = 'validation:dateMustBeBeforeSpecifiedDate' }) =>
		({ fieldValue, formData }) => {
			if (!fieldValue) {
				return null;
			}

			if (!formData[field]) {
				return null;
			}

			const selectedDate = startOfDay(new Date(fieldValue));
			const dateToCompare = startOfDay(new Date(formData[field]));
			const dateIsValid = isSameDay(selectedDate, dateToCompare) || isBefore(selectedDate, dateToCompare);
			const finalMessage = getLabelMessage(messageKey, { field: fieldLocalization });

			if (!dateIsValid) {
				return finalMessage;
			} else {
				return null;
			}
		},
);

export const isPositiveNumber = createValidationFn(
	({ messageKey = 'validation:nonPositiveNumber' }) =>
		({ fieldValue }) => {
			if (!fieldValue) {
				return null;
			}

			return ifElse(
				allPass([Number.isInteger, gt(__, 0)]),
				always(false),
				always(messageKey),
			)(Number(fieldValue));
		},
);

export const isPositivePercentage = createValidationFn(
	({ messageKey = 'validation:wrongPercentage' }) =>
		({ fieldValue }) => {
			if (!fieldValue) {
				return null;
			}

			return ifElse(
				allPass([Number.isInteger, gt(__, 0), lte(__, 100)]),
				always(false),
				always(messageKey),
			)(Number(fieldValue));
		},
);
